import { failureCode } from 'shared/constants.json';
import { hasSignedRequest, token } from '@/lib/signed-request';
import { isNullOrUndefined } from 'shared/lib/object/object';

const REQUEST_ID_HEADER = 'x-request-id';
const INSTANCE_ID_HEADER = 'x-instance-id';
const BAGGAGE_HEADER = 'baggage';
const TENANT_ID_HEADER = 'x-tenant-id';

// Generate instance ID once per page load
const generateID = () => btoa([...Array(2).keys()].map(() => Math.random() * Number.MAX_SAFE_INTEGER));
export const instanceId = generateID();

// The instance id is echoed back on subscription events so the data-sync can
// recognise its own mutations (data.instanceId === instanceId) and skip them as
// self-events -- we already applied the result locally from the mutation response.
// That suppression is only correct for the generic graphmutation endpoints, whose
// callers apply the returned entities to their stores. Endpoints that mutate but do
// NOT apply the result locally (e.g. csv-import) rely entirely on the subscription to
// surface the change; tagging them would make the subscription drop their own events
// and the initiating tab would go stale until a reload. So only these endpoints carry
// the instance id; everything else stays untagged and is delivered live.
const SELF_EVENT_ENDPOINTS = ['mutate', 'mutate-single', 'create', 'create-single', 'update', 'update-single'];

const shouldTrackEvents = (config) => {
  const [action] = (config.url || '').split('?')[0].split('/').filter((segment) => segment.length > 0);
  return SELF_EVENT_ENDPOINTS.includes(action);
};

export const installRequestID = (config) => {
  const requestId = generateID();
  let baggage = `lagoon.requestId=${requestId},lagoon.instanceId=${instanceId}`;
  try {
    const baggageHeader = config.headers.get(BAGGAGE_HEADER);
    baggage = baggageHeader ? `${baggageHeader},${baggage}` : baggage;
  } catch (error) { /* empty */ }

  return {
    ...config,
    headers: {
      ...config.headers,
      [`${REQUEST_ID_HEADER}`]: requestId,
      ...(shouldTrackEvents(config) ? { [`${INSTANCE_ID_HEADER}`]: instanceId } : {}),
      [`${BAGGAGE_HEADER}`]: baggage,
    },
  };
};

export const installTenantID = (store) => (config) => {
  // Prefer the tenant DISCOVER_TENANT resolved (available before any account is fetched)
  const tenantId = store.getters?.resolvedTenantId ?? store.getters?.loggedInUserAccount?.uid;
  if (isNullOrUndefined(tenantId)) {
    return config;
  }
  return {
    ...config,
    headers: {
      ...config.headers,
      [`${TENANT_ID_HEADER}`]: String(tenantId),
    },
  };
};

const statusForbidden = 403;

export const detectStepUp = (store, { status, data }) => {
  if (status === statusForbidden && data?.code === failureCode.stepUpRequired) {
    store.commit('STEP_UP_REQUIRED', data.payload);
  }
};

export const installSignedRequestAuth = (config) => {
  const url = new URL(`${config.baseURL}${config.url}`, window.location.origin).pathname;
  if (!hasSignedRequest(window.location.hash, url)) {
    return config;
  }
  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token(window.location.hash, url)}`,
    },
  };
};
